"use client";
import type { ReactElement } from "react";

type Props = {
  onRetry: () => void;
};

/**
 * The recovery panel for global-error.tsx — the boundary that replaces the
 * whole document when the root layout itself throws.
 *
 * Deliberately styled inline rather than with Tailwind or the shared
 * ErrorState: it renders when the root layout, the module that imports the
 * stylesheet, is the thing that failed, so a class-based panel could come out
 * as unstyled text on top of an already-bad moment. The inline styles mirror
 * ErrorState closely enough that a lead cannot tell which of the two they
 * landed on.
 */
export function FatalErrorPanel({ onRetry }: Props): ReactElement {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#ffffff",
        padding: "24px",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: "28rem" }}>
        <div
          style={{
            width: "64px",
            height: "64px",
            margin: "0 auto 16px",
            borderRadius: "9999px",
            backgroundColor: "#fef2f2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#f87171"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
            />
          </svg>
        </div>
        {/* Same copy as ErrorState's UNEXPECTED_ERROR, on purpose: a lead who
            hits both should not be told two different things. */}
        <h2
          style={{
            margin: "0 0 8px",
            fontSize: "18px",
            fontWeight: 600,
            color: "#262626",
          }}
        >
          Something went wrong. Please try again.
        </h2>
        <button
          onClick={onRetry}
          style={{
            marginTop: "16px",
            border: "none",
            borderRadius: "9999px",
            backgroundColor: "#029391",
            color: "#ffffff",
            padding: "8px 24px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
